/**
 * Helpers de formateo de fechas y nombres comunes en la UI.
 *
 * El backend devuelve timestamps UTC NAIVE (sin sufijo Z ni offset),
 * a veces con espacio en vez de 'T' (ej. "2026-05-28 12:23:08.642788").
 * JS Date() interpreta esos strings como hora LOCAL del browser, lo que
 * en zonas != UTC produce desfases (ej. "-13739s atrás" en Bogota UTC-5).
 *
 * ``parseBackendDate`` normaliza: si el string no trae tz explicita,
 * lo trata como UTC.
 */

function parseBackendDate(iso: string): Date {
  let s = iso.trim();
  // Espacio -> 'T' para ISO valido
  if (s.includes(" ") && !s.includes("T")) {
    s = s.replace(" ", "T");
  }
  // Si NO tiene tz (Z o +HH / -HH al final), asumir UTC
  const hasTz = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  if (!hasTz) {
    s = s + "Z";
  }
  return new Date(s);
}

export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = parseBackendDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Hora Bogotá SIEMPRE (orden Carlos 2026-07-15): toda fecha visible de la
  // plataforma se alinea a America/Bogota, sin depender del reloj del browser.
  return d.toLocaleString("es-CO", {
    timeZone: "America/Bogota",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Solo la fecha (sin hora), en hora Bogotá. */
export function formatDateOnly(iso: string | null): string {
  if (!iso) return "—";
  const d = parseBackendDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-CO", {
    timeZone: "America/Bogota",
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

export function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const d = parseBackendDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  // Tolerancia: timestamps levemente futuros (clock skew) -> "hace un momento"
  if (diffMs < 0) return "hace un momento";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s atrás`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m atrás`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h atrás`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d atrás`;
  return formatDate(iso);
}

export function truncate(s: string | null, max: number): string {
  if (!s) return "";
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}

/**
 * Nombre largo/descriptivo de la HT, derivado de `ht_path`.
 *
 * El `ht_code` es el canónico ÚNICO y corto (`FAT-003`) en todo el sistema;
 * el nombre del directorio (`HT_FAT_003_Facturacion`) vive en el path y se usa
 * SOLO para legibilidad en listados (Carlos 2026-07-17).
 */
export function htNombreLargo(htPath: string | null | undefined): string {
  if (!htPath) return "";
  return htPath.replace(/\/+$/, "").split("/").pop() ?? "";
}
